import React, { useEffect, useRef, useState } from 'react';
import { Download, ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  onExportCSV?: () => void;
  onExportExcel?: () => void;
  disabled?: boolean;
}

const menuItemStyle: React.CSSProperties = {
  width: '100%',
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-4)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-gray-700)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
};

export const ExportDropdown: React.FC<Props> = ({ onExportCSV, onExportExcel, disabled }) => {
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const trigger = (fn?: () => void) => {
    setOpen(false);
    if (fn) {
      fn();
    } else {
      setNotice(true);
      window.setTimeout(() => setNotice(false), 3000);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="btn btn-secondary"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Download size={18} style={{ marginRight: 'var(--spacing-2)' }} />
        Export
        {open ? (
          <ChevronUp size={16} style={{ marginLeft: 'var(--spacing-1)' }} />
        ) : (
          <ChevronDown size={16} style={{ marginLeft: 'var(--spacing-1)' }} />
        )}
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 'var(--spacing-1)',
            background: '#ffffff',
            border: '1px solid var(--color-gray-300)',
            borderRadius: 'var(--radius-md, 4px)',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 50,
            minWidth: '140px',
            overflow: 'hidden',
          }}
        >
          <button
            role="menuitem"
            onClick={() => trigger(onExportCSV)}
            style={{ ...menuItemStyle, borderBottom: '1px solid var(--color-gray-200)' }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-gray-100)')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            CSV
          </button>
          <button
            role="menuitem"
            onClick={() => trigger(onExportExcel)}
            style={menuItemStyle}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-gray-100)')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            Excel
          </button>
        </div>
      )}
      {notice && (
        <div
          role="status"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 'var(--spacing-1)',
            background: '#FEF3C7',
            border: '1px solid #F59E0B',
            borderRadius: 'var(--radius-md, 4px)',
            padding: 'var(--spacing-2) var(--spacing-3)',
            fontSize: 'var(--text-xs)',
            color: '#92400E',
            whiteSpace: 'nowrap',
            zIndex: 51,
          }}
        >
          Export not available yet for this format.
        </div>
      )}
    </div>
  );
};
